import { useCallback } from 'react';
import createClient from 'openapi-fetch';
import { paths } from '@/api/openapi/schema';
import { auth } from '@/api/openapi/middleware';
import { useSession } from '@/providers/session/SessionProvider';
import { AI_VOICE_MAPPING } from '@/constant';

export interface UseAudioGeneratorReturn {
    generateAudio: (voice: string, text: string) => Promise<File>;
}

export const useAudioGenerator = (): UseAudioGeneratorReturn => {
    const { session } = useSession();

    const generateAudio = useCallback(
        async (voice: string, text: string): Promise<File> => {
            // Create the API client and send the assessment request
            const client = createClient<paths>({ baseUrl: `${import.meta.env.VITE_API_SERVICE_URL}/v1` });
            client.use(auth(session?.token));

            const response = await client.POST('/experiments/generators/audios', {
                body: {
                    text: text,
                    voiceName: AI_VOICE_MAPPING[voice]?.voiceName || '',
                    role: AI_VOICE_MAPPING[voice]?.role,
                    style: AI_VOICE_MAPPING[voice]?.style,
                    rate: AI_VOICE_MAPPING[voice]?.rate,
                    pitch: AI_VOICE_MAPPING[voice]?.pitch,
                },
                parseAs: 'arrayBuffer',
            });

            if (response.error) {
                throw response.error.error;
            }

            if (!response.data) {
                throw new Error('No audio data received');
            }

            const audioFile = new File([response.data], 'speech.mp3', { type: 'audio/mpeg' });
            if (!audioFile) {
                throw new Error('No valid audio file selected');
            }

            return audioFile;
        },
        [session],
    );

    return {
        generateAudio,
    };
};
